import React, { useContext, useEffect, useState } from "react"
import { Col, Row } from "react-bootstrap"
import InputRange from "react-input-range"
import { GlobalAuthContext } from "../../../context/authContext"
import { formatDateToBackend } from "../../../utils/format-date-time"
import { formatIndustrySkills } from "../../../utils/format-industry-skills"
import {
  formatDataForSelect,
  formatSkillsForSelect,
} from "../../../utils/format-skills-for-select"
import { formatCountryToDistrict } from "../../../utils/formatCountryToDistrict"
import { formatMultipleCheckboxData } from "../../../utils/formatMultipleCheckboxData"
import {
  formatSalaryWithComma,
  formatSalaryWithoutComma,
} from "../../../utils/formatSalary"
import getCountryData from "../../../utils/get-country-data"
import ErrorBlock from "../../error-block"
import Datepicker from "../../input/datepicker"

import InputTextBox from "../../input/input-text-box"
import MinMaxInput from "../../input/min-max-input"
import MultipleCheckbox from "../../input/multiple-checkbox"
import TagInput from "../../input/tag-input"
import TextArea from "../../input/textarea"
import InputBox from "../../input/wdn-input-box"

import "./job-posting.scss"
import { JOB_POSTING_FORM_SUBMIT } from "./jobActions"

const JobPostingForm = ({ data }) => {
  const { state } = useContext(GlobalAuthContext)

  const getNameLabelValueId = (name, id) => ({
    name,
    id,
    value: id,
    label: name,
  })

  const getDistrictList = formatCountryToDistrict(
    state?.config?.country,
    state?.profile?.country
  )

  const getDistrictData = (districts, id) => {
    const value = districts?.filter(
      district => district?.id?.toString() === id?.toString()
    )
    return value?.[0]?.district
  }

  const getInitialState = () => ({
    company_id: state?.user?.company?.id,
    title: data?.title ?? "",
    industry_id: [data?.industry_id ?? state?.user?.company?.industry_id],
    industry: [
      getNameLabelValueId(
        data?.industry ?? state?.profile?.industry_name,
        data?.industry_id ?? state?.profile?.industry
      ),
    ],
    preferred_location: [
      getNameLabelValueId(
        getDistrictData(
          getDistrictList,
          data?.district_id ?? state?.company_data?.contact?.city
        ),
        data?.district_id ?? state?.company_data?.contact?.city
      ),
    ],
    preferred_location_id: [
      data?.district_id ?? state?.company_data?.contact?.city,
    ],
    job_type_id: data?.job_type_id ? [data?.job_type_id] : [2],
    job_type: [
      getNameLabelValueId(
        data?.job_type ?? "Full Time",
        data?.job_type_id ?? 2
      ),
    ],
    job_shift_id: data?.job_shift_id ? [data?.job_shift_id] : [2],
    job_shift: [
      getNameLabelValueId(data?.job_shift ?? "Day", data?.job_shift_id ?? 2),
    ],
    education_level_id: data?.education_level_id ?? "",
    address: data?.address ?? "",
    experience: {
      min: data?.min_exp ?? 0,
      max: data?.max_exp ?? 5,
    },
    min_exp: data?.min_exp ?? 0,
    max_exp: data?.max_exp ?? 5,
    salary: {
      min: data?.min_salary ?? "",
      max: data?.max_salary ?? "",
    },
    max_salary: data?.max_salary ?? "",
    min_salary: data?.min_salary ?? "",
    salary_negotiable: 0,
    skills: data?.skills?.map(skill => skill?.id) ?? [],
    skills_list: [],
    selected_skills: data?.skills ? formatSkillsForSelect(data?.skills) : [],
    end_date: data?.end_date ?? "",
    end_date_display: data?.end_date ? new Date(data?.end_date) : "",
    job_description: data?.job_description ?? "",
    gender_id: data?.gender?.id ?? 4,
    no_of_employees_display: data?.no_of_employees ?? 1,
    no_of_employees: data?.no_of_employees ?? 1,
  })

  const [jobPostingFormData, setJobPostingFormData] = useState(
    getInitialState()
  )

  const [errors, setErrors] = useState({})

  const handleFocus = () => {
    setErrors({})
  }

  const handleChange = (name, value) => {
    if (name === "gender_id") setErrors({})
    if (name === "end_date") {
      return setJobPostingFormData({
        ...jobPostingFormData,
        end_date: formatDateToBackend(value),
        end_date_display: value,
      })
    }
    if (name === "no_of_employees") {
      let num = formatSalaryWithoutComma(value)
      if (num === 0) num = 1
      return setJobPostingFormData({
        ...jobPostingFormData,
        no_of_employees: num,
        no_of_employees_display: formatSalaryWithComma(num),
      })
    }
    setJobPostingFormData({
      ...jobPostingFormData,
      [name]: value,
    })
  }

  const current_country_data = getCountryData(
    state?.config?.country,
    state?.profile?.country
  )

  const handleSalaryChange = (name, value) => {
    const minSalary = current_country_data?.minSalary
    const val = formatSalaryWithoutComma(value)
    if (isNaN(Number(val)) && !!val) return null
    if (name === "min") {
      const salary = {
        ...jobPostingFormData.salary,
        min: formatSalaryWithComma(val),
      }
      setJobPostingFormData({ ...jobPostingFormData, salary, min_salary: val })
    }
    if (name === "max") {
      const salary = {
        ...jobPostingFormData.salary,
        max: formatSalaryWithComma(val),
      }
      setJobPostingFormData({ ...jobPostingFormData, salary, max_salary: val })
    }
  }

  const handleMultipleCheckboxChange = (tag, name) => {
    const list_of_ids = tag.map(data => data.id)
    setJobPostingFormData({
      ...jobPostingFormData,
      [name]: tag,
      [name + "_id"]: list_of_ids,
    })
  }

  const handleSkillsChange = tag => {
    const list_of_ids = tag.map(data => data.value)
    setJobPostingFormData({
      ...jobPostingFormData,
      skills: list_of_ids,
      selected_skills: tag,
    })
  }

  const getMaxDay = () => {
    let date = new Date()
    date.setDate(date.getDate() + 30)
    return date
  }

  const handleCheckboxChange = value => {
    setJobPostingFormData({
      ...jobPostingFormData,
      salary_negotiable: value === 1 ? 0 : 1,
    })
  }

  const handleExperienceChange = value =>
    setJobPostingFormData({
      ...jobPostingFormData,
      experience: value,
      min_exp: value.min,
      max_exp: value.max,
    })

  const handleSkillsFocus = () => {
    setErrors({})

    setJobPostingFormData({ ...jobPostingFormData, skills_list: [] })
    fetchSkills()
  }

  const fetchSkills = () => {
    const data = formatIndustrySkills(
      state?.industry_skills,
      jobPostingFormData?.industry_id
    )
    setJobPostingFormData({ ...jobPostingFormData, skills_list: data })
  }

  const id = data?.id
  const handlePostClick = async e => {
    e.preventDefault()
    setErrors({})
    if (jobPostingFormData?.min_salary < current_country_data?.minSalary) {
      setErrors({ min_salary: "min" })
      return
    } else if (jobPostingFormData?.max_salary < jobPostingFormData?.min_salary) {
      setErrors({ max_salary: "min" })
      return
    }
    setJobPostingFormData({ ...jobPostingFormData, posting: true })
    const data = formatDataForJobsBackend(jobPostingFormData)
    try {
      const res = await JOB_POSTING_FORM_SUBMIT(data, id)
      if (res.status === 201 && res?.data) {
        setErrors({ success: "Job Posted successfully" })
        setJobPostingFormData(getInitialState())
        return
      }
      if (res.status === 200 && res?.data) {
        setErrors({ success: "Job Details Updated successfully" })
      }
    } catch (error) {
      if (error?.response?.data) setErrors(error?.response?.data)
    }
    setJobPostingFormData({ ...jobPostingFormData, posting: false })
  }
  const handleSaveAsDraftClick = async e => {
    e.preventDefault()
  }

  const formatDataForJobsBackend = data => {
    return {
      ...data,
      industry_id: data?.industry_id?.[0],
      preferred_location_id: data?.preferred_location_id?.[0],
      job_type_id: data?.job_type_id?.[0],
      job_shift_id: data?.job_shift_id?.[0],
      skills: data?.selected_skills?.map(skill => skill.id),
    }
  }

  useEffect(() => { }, [])
  return (
    <form className="job-form">
      <Row>
        <Col lg="6">
          <InputTextBox
            name="title"
            onFocus={handleFocus}
            class={errors?.title && "error"}
            placeholder="Designer"
            value={jobPostingFormData?.title}
            label={
              <>
                Job Title<span>*</span>
              </>
            }
            onChange={value => handleChange("title", value)}
          />
        </Col>
        <Col lg="6">
          <InputTextBox
            type="text"
            name="no_of_employees"
            min={1}
            onFocus={handleFocus}
            class={errors?.no_of_employees && "error"}
            placeholder="1"
            value={jobPostingFormData?.no_of_employees_display}
            label={
              <>
                No of Candidates<span>*</span>
              </>
            }
            onChange={value => handleChange("no_of_employees", value)}
          />
        </Col>
        <Col lg="6">
          <MultipleCheckbox
            name="industry_id"
            styles={errors?.industry_id ? `error` : ``}
            options={formatMultipleCheckboxData(
              jobPostingFormData.industry,
              formatDataForSelect(state?.config?.industry)
            )}
            value={jobPostingFormData.industry}
            extra={
              jobPostingFormData.industry?.length > 0
                ? jobPostingFormData.industry.length + " selected"
                : undefined
            }
            onFocus={handleFocus}
            label={
              <>
                Job Industry
                <span>*</span>
              </>
            }
            placeholder={"Industry"}
            handleChange={tag => handleMultipleCheckboxChange(tag, "industry")}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            menuOpenOnFocus
            checkBoxOption
            withCheckbox
            isMulti
            isSearchable
            isClearable
          />
        </Col>
        <Col lg="6">
          <InputBox
            type="radio"
            onFocus={handleFocus}
            name="gender_id"
            onChange={value => handleChange("gender_id", value.target.value)}
            styles={
              errors.gender_id
                ? "rounded-border outline-faded error"
                : "rounded-border outline-faded"
            }
            optionHeader="Select"
            key_value="name"
            value={jobPostingFormData.gender_id}
            options={state?.config?.gender}
            label={
              <>
                Gender<span>*</span>
              </>
            }
          />
        </Col>
        <Col lg="6">
          <MultipleCheckbox
            name="location"
            styles={
              errors?.preferred_location_id
                ? ` outline-faded error`
                : ` outline-faded`
            }
            options={formatMultipleCheckboxData(
              jobPostingFormData.preferred_location,
              formatDataForSelect(getDistrictList, "district")
            )}
            value={jobPostingFormData.preferred_location}
            extra={
              jobPostingFormData.preferred_location?.length > 0
                ? jobPostingFormData.preferred_location?.length + " selected"
                : undefined
            }
            onFocus={handleFocus}
            label={
              <>
                Preferred Location<span>*</span>
              </>
            }
            placeholder={"Location"}
            handleChange={tag =>
              handleMultipleCheckboxChange(tag, "preferred_location")
            }
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            menuOpenOnFocus
            checkBoxOption
            withCheckbox
            isMulti
            isSearchable
            isClearable
          />
        </Col>
        <Col lg="6">
          <Row>
            <MinMaxInput
              label={
                <>
                  Expected Salary<span>*</span>
                </>
              }
              minType="text"
              maxType="text"
              minName="minSalary"
              maxName="maxSalary"
              minOnFocus={handleFocus}
              maxOnFocus={handleFocus}
              minId={"minSalary"}
              maxId={"maxSalary"}
              minMin={current_country_data?.minSalary}
              maxMin={Number(current_country_data?.minSalary) + 1}
              minOnChange={value => handleSalaryChange("min", value)}
              maxOnChange={value => handleSalaryChange("max", value)}
              handleCheckboxChange={handleCheckboxChange}
              minStyles={`rounded-border minSalary allow-focus ${errors?.min_salary ? " error" : ""
                }`}
              maxStyles={`rounded-border maxSalary allow-focus ${errors?.max_salary ? " error" : ""
                }`}
              minValue={jobPostingFormData.salary.min}
              maxValue={jobPostingFormData.salary.max}
              hideMaxLabel="hide-label"
              minPlaceholder={"Min"}
              maxPlaceholder={"Max"}
            />
          </Row>
        </Col>
        <Col lg="6">
          <MultipleCheckbox
            name="work_type"
            styles={
              errors?.job_type_id ? ` outline-faded error` : ` outline-faded`
            }
            options={formatMultipleCheckboxData(
              jobPostingFormData.job_type,
              formatDataForSelect(state?.config?.job_type, "name")
            )}
            value={jobPostingFormData.job_type}
            extra={
              jobPostingFormData.job_type?.length > 0
                ? jobPostingFormData.job_type?.length + " selected"
                : undefined
            }
            onFocus={handleFocus}
            label={
              <>
                Work Type Preferred
                <span>*</span>
              </>
            }
            placeholder={"Select"}
            handleChange={tag => handleMultipleCheckboxChange(tag, "job_type")}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            menuOpenOnFocus
            checkBoxOption
            withCheckbox
            isMulti
            isSearchable
            isClearable
          />
        </Col>
        <Col lg="6">
          <MultipleCheckbox
            name="work_shift"
            styles={
              errors.job_shift_id ? ` outline-faded error` : ` outline-faded`
            }
            options={formatMultipleCheckboxData(
              jobPostingFormData.job_shift,
              formatDataForSelect(state?.config?.shift, "name")
            )}
            value={jobPostingFormData.job_shift}
            extra={
              jobPostingFormData.job_shift?.length > 0
                ? jobPostingFormData.job_shift?.length + " selected"
                : undefined
            }
            onFocus={handleFocus}
            label={
              <>
                Work Shift Preferred
                <span>*</span>
              </>
            }
            placeholder={"Select"}
            handleChange={tag => handleMultipleCheckboxChange(tag, "job_shift")}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            menuOpenOnFocus
            checkBoxOption
            withCheckbox
            isMulti
            isSearchable
            isClearable
          />
        </Col>
        <Col lg="6">
          <InputBox
            type="select"
            name="education"
            onChange={value => handleChange("education_level_id", value)}
            styles={
              errors?.education_level_id
                ? " error rounded-border"
                : "rounded-border"
            }
            onFocus={handleFocus}
            optionHeader={"Select"}
            value={jobPostingFormData?.education_level_id}
            key_value="level"
            options={state?.config?.education_level}
            label={
              <>
                Education
                <span>*</span>
              </>
            }
          />
        </Col>
        <Col lg="6">
          <InputTextBox
            name="address"
            onFocus={handleFocus}
            class={errors.address && "error"}
            placeholder="Address"
            value={jobPostingFormData?.address}
            label={
              <>
                Address
              </>
            }
            onChange={value => handleChange("address", value)}
          />
        </Col>
        <Col lg="6">
          <div className="form-group">
            <label>
              Experience<span>*</span>
            </label>
            <div className="form-group-input-range">
              <InputRange
                maxValue={Number(state?.config?.experience?.length) - 1}
                minValue={0}
                formatLabel={value => `${value}yrs`}
                value={{
                  min: Number(jobPostingFormData?.experience?.min),
                  max: Number(jobPostingFormData?.experience?.max),
                }}
                onChange={value => handleExperienceChange(value)}
              // onChangeComplete={value => {
              //   handleExperienceChange(value)
              // }}
              />
            </div>
          </div>
        </Col>
        <Col lg="6">
          <Datepicker
            className={
              errors?.end_date
                ? "form-group  error position-relative w-100"
                : "form-group position-relative w-100"
            }
            label={
              <>
                End Date
                <span>*</span>
              </>
            }
            class={"form-control rounded-border outline-faded"}
            name="end_date"
            onFocus={handleFocus}
            placeholderText="DD-MM-YYYY"
            selected={jobPostingFormData.end_date_display}
            minDate={new Date()}
            maxDate={getMaxDay()}
            onChange={value => handleChange("end_date", value)}
            dateFormat="yyyy-MM-dd"
          />
        </Col>
        <Col lg="12">
          <TagInput
            name="skills"
            styles={errors?.skills ? ` outline-faded error` : ` outline-faded`}
            options={formatDataForSelect(jobPostingFormData?.skills_list)}
            value={jobPostingFormData.selected_skills}
            onFocus={handleSkillsFocus}
            isMulti
            label={
              <>
                Skills
                <span>*</span>
              </>
            }
            useLabel="skills"
            placeholder={"Type and Select..."}
            useOnChange={value => handleSkillsChange(value)}
            loadingPlaceholder={
              jobPostingFormData?.industry_id?.length < 1
                ? "Please select industry first"
                : "Loading..."
            }
          />
        </Col>
        <Col lg="12">
          <TextArea
            editor
            total={5000}
            onChange={value => handleChange("job_description", value)}
            onFocus={handleFocus}
            label={
              <>
                Job Description
                <span>*</span>
              </>
            }
            class={errors.job_description && "error"}
            value={jobPostingFormData?.job_description}
            placeholder="Describe your JD/JS"
          />
        </Col>
        <Col lg="12">
          {Object.keys(errors)?.length > 0 && <ErrorBlock errors={errors} />}
        </Col>
        <Col lg={{ span: "3" }}>
          <button
            className="btn w-100 btn-custom p-3  rounded primary my-2"
            onClick={handlePostClick}
            disabled={jobPostingFormData?.posting}
          >
            {data?.id
              ? jobPostingFormData?.posting
                ? "Updating..."
                : "Update"
              : jobPostingFormData?.posting
                ? "Posting..."
                : "Post"}
          </button>
        </Col>
        {false && !!!data?.id ? (
          <Col lg={{ span: "3" }}>
            <button
              onClick={handleSaveAsDraftClick}
              className="btn w-100 btn-custom p-3  rounded blue-outline my-2"
              disabled={jobPostingFormData?.saving_as_draft}
            >
              {jobPostingFormData?.saving_as_draft
                ? "Saving..."
                : "Save As Draft"}
            </button>
          </Col>
        ) : (
          <></>
        )}
      </Row>
    </form>
  )
}

export default JobPostingForm
